import { animate, style, transition, trigger } from '@angular/animations';
import { Component, computed, inject, signal, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from 'app/core/auth/auth.service';
import { PwaInstallService } from 'app/core/pwa/pwa-install.service';

/**
 * Invites the user to keep the app on the home screen.
 *
 * The invitation is only raised once the user is signed in: it is the moment the app becomes worth
 * installing, and it keeps the banner away from the sign-in form, which is the screen the on-screen
 * keyboard leaves the least room for.
 */
@Component({
    selector: 'pwa-install-prompt',
    templateUrl: './install-prompt.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [MatButtonModule, MatIconModule],
    animations: [
        trigger('slideIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(1rem)' }),
                animate('300ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1, transform: 'none' })),
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(1rem)' })),
            ]),
        ]),
    ],
})
export class PwaInstallPromptComponent {
    private readonly _authService = inject(AuthService);
    private readonly _pwaInstallService = inject(PwaInstallService);

    /** Whether the manual iOS steps are unfolded. */
    protected readonly showIosSteps = signal(false);

    protected readonly ios = this._pwaInstallService.ios;

    protected readonly visible = computed(
        () => !!this._authService.user() && this._pwaInstallService.shouldPromptInstall()
    );

    protected async install(): Promise<void> {
        if (this._pwaInstallService.canInstall()) {
            await this._pwaInstallService.install();
            return;
        }

        // iOS has no programmatic installation: the user has to go through the share sheet.
        this.showIosSteps.update((shown) => !shown);
    }

    protected dismiss(): void {
        this._pwaInstallService.dismiss();
    }
}
